/**
 * Typed client for `GET /api/user-directory` — the lightweight people
 * directory open to every logged-in user (user decision 2026-10-12).
 *
 * Use it wherever a widget only needs to turn a user id into a name (calendar
 * rows, filter dropdowns, the command palette, voter chips, owner pickers).
 * The admin `GET /api/users` listing is `users:view` GLOBAL only since the
 * RBAC scope fix and 403s for most roles — never call it for name lookups.
 */
import { apiService } from './api';

export interface UserDirectoryEntry {
  _id: string;
  id: string;
  name: string;
  email: string;
  avatar?: string;
  department?: string;
  isActive: boolean;
  /**
   * Only with `include: 'pods'` (and only for callers holding `pods:browse`):
   * pod ids of the user's active assignments, earliest start first.
   */
  podIds?: string[];
}

export interface UserDirectoryParams {
  /** Case-insensitive name/email search (literal, not a regex). */
  q?: string;
  /** Restrict to these user ids. */
  ids?: string[];
  /** Include deactivated users — honoured only for callers with `users:view` global. */
  includeInactive?: boolean;
  /** Page size (server default 500, max 2000). */
  limit?: number;
  /**
   * `'pods'` attaches `podIds` to each entry (silently omitted for callers
   * without `pods:browse`). Changes the entry shape — give such queries their
   * own React Query key, never share it with plain directory lookups.
   */
  include?: 'pods';
}

type DirectoryEnvelope = { success: boolean; result: { users: UserDirectoryEntry[] } };

/** React Query key prefix for directory lookups — share it so widgets reuse one cache entry. */
export const USER_DIRECTORY_QUERY_KEY = 'user-directory';

export async function getUserDirectory(params: UserDirectoryParams = {}): Promise<UserDirectoryEntry[]> {
  const query: Record<string, string | number> = {};
  if (params.q) query.q = params.q;
  if (params.ids && params.ids.length > 0) query.ids = params.ids.join(',');
  if (params.includeInactive) query.includeInactive = 'true';
  if (params.limit) query.limit = params.limit;
  if (params.include) query.include = params.include;

  const body = (await apiService.get<never>('/user-directory', query)) as unknown as DirectoryEnvelope;
  return body.result?.users ?? [];
}
